import { gql } from '@apollo/client';

/**
 * ТЗ 0122 — почтовый клиент ERP. Отказ приходит `ok: false` + машинный
 * `errorCode` (фронт переводит), а не исключением. Имена операций ≠ полям
 * бэкенда.
 */

export const RunMailThreadsActionMutation = gql`
  mutation RunMailThreadsAction(
    $threadIds: [ID!]!
    $action: MailThreadAction!
  ) {
    mailThreadsAction(threadIds: $threadIds, action: $action) {
      ok
      error
      errorCode
      count
    }
  }
`;

export const ApplyMailThreadsLabelMutation = gql`
  mutation ApplyMailThreadsLabel(
    $threadIds: [ID!]!
    $addLabelIds: [String!]
    $removeLabelIds: [String!]
  ) {
    mailThreadsLabel(
      threadIds: $threadIds
      addLabelIds: $addLabelIds
      removeLabelIds: $removeLabelIds
    ) {
      ok
      error
      errorCode
      count
    }
  }
`;

export const SnoozeMailThreadsMutation = gql`
  mutation SnoozeMailThreads($threadIds: [ID!]!, $until: DateTime!) {
    mailSnooze(threadIds: $threadIds, until: $until) {
      ok
      error
      errorCode
      count
    }
  }
`;

export const UnsnoozeMailThreadsMutation = gql`
  mutation UnsnoozeMailThreads($threadIds: [ID!]!) {
    mailUnsnooze(threadIds: $threadIds) {
      ok
      error
      errorCode
      count
    }
  }
`;

export const MarkMailMessagesReadMutation = gql`
  mutation MarkMailMessagesRead($messageIds: [ID!]!, $read: Boolean!) {
    mailMarkMessagesRead(messageIds: $messageIds, read: $read) {
      ok
      error
      errorCode
      count
    }
  }
`;

export const AddMailLabelMutation = gql`
  mutation AddMailLabel($mailboxId: ID!, $name: String!, $colorBg: String) {
    mailCreateLabel(mailboxId: $mailboxId, name: $name, colorBg: $colorBg) {
      ok
      error
      errorCode
      label {
        ...MailLabelFields
      }
    }
  }
`;

export const EditMailLabelMutation = gql`
  mutation EditMailLabel($labelId: ID!, $name: String, $colorBg: String) {
    mailUpdateLabel(labelId: $labelId, name: $name, colorBg: $colorBg) {
      ok
      error
      errorCode
      label {
        ...MailLabelFields
      }
    }
  }
`;

export const RemoveMailLabelMutation = gql`
  mutation RemoveMailLabel($labelId: ID!) {
    mailDeleteLabel(labelId: $labelId) {
      ok
      error
      errorCode
      count
    }
  }
`;

export const StoreMailDraftMutation = gql`
  mutation StoreMailDraft($input: MailComposeInput!) {
    mailSaveDraft(input: $input) {
      ok
      error
      errorCode
      draft {
        ...MailDraftFields
      }
    }
  }
`;

export const DiscardMailDraftMutation = gql`
  mutation DiscardMailDraft($draftId: ID!) {
    mailDeleteDraft(draftId: $draftId) {
      ok
      error
      errorCode
      count
    }
  }
`;

export const SendMailMessageMutation = gql`
  mutation SendMailMessage($input: MailComposeInput!, $sendAt: DateTime) {
    mailSend(input: $input, sendAt: $sendAt) {
      ok
      error
      errorCode
      threadId
      messageId
      scheduledId
    }
  }
`;

export const CancelScheduledMailMutation = gql`
  mutation CancelScheduledMail($id: ID!) {
    mailCancelScheduled(id: $id) {
      ok
      error
      errorCode
      count
    }
  }
`;

export const RequestMailSyncMutation = gql`
  mutation RequestMailSync($mailboxId: ID) {
    mailSyncNow(mailboxId: $mailboxId) {
      ok
      error
      errorCode
      count
    }
  }
`;

/** ТЗ 0122 — догрузить старую историю (только Admin). */
export const StartMailHistoryLoadMutation = gql`
  mutation StartMailHistoryLoad(
    $mailboxId: ID
    $days: Int
    $since: Date
    $allHistory: Boolean
  ) {
    mailLoadHistory(
      mailboxId: $mailboxId
      days: $days
      since: $since
      allHistory: $allHistory
    ) {
      ok
      error
      errorCode
      count
    }
  }
`;

export const ImportMailboxSignaturesMutation = gql`
  mutation ImportMailboxSignatures {
    mailImportSignatures {
      ok
      error
      errorCode
      count
    }
  }
`;

export const SuggestMailAiReplyMutation = gql`
  mutation SuggestMailAiReply(
    $threadId: ID!
    $instruction: String
    $tone: MailAiTone
  ) {
    mailAiReply(threadId: $threadId, instruction: $instruction, tone: $tone) {
      ok
      error
      errorCode
      reply {
        ...MailAiReplyFields
      }
    }
  }
`;

/** Разбор треда ИИ (`force` — заново, мимо кеша). */
export const AnalyzeMailThreadMutation = gql`
  mutation AnalyzeMailThread($threadId: ID!, $force: Boolean) {
    mailAiAnalyze(threadId: $threadId, force: $force) {
      ok
      error
      errorCode
      insight {
        ...MailInsightFields
      }
    }
  }
`;

/** «ИИ: улучшить текст» в редакторе письма. */
export const RewriteMailTextMutation = gql`
  mutation RewriteMailText(
    $mailboxId: ID!
    $html: String!
    $mode: MailRewriteMode!
  ) {
    mailAiRewrite(mailboxId: $mailboxId, html: $html, mode: $mode) {
      ok
      error
      errorCode
      html
      suggestionId
    }
  }
`;

/** Учёт черновика ИИ: used | edited | dismissed. */
export const MarkMailAiSuggestionMutation = gql`
  mutation MarkMailAiSuggestion($suggestionId: ID!, $outcome: String!) {
    mailAiMarkSuggestion(suggestionId: $suggestionId, outcome: $outcome) {
      ok
      error
      errorCode
      count
    }
  }
`;

/** Задача из письма (связи и «убрать из входящих» — на сервере). */
export const CreateTaskFromMailMutation = gql`
  mutation CreateTaskFromMail(
    $threadId: ID!
    $title: String!
    $description: String
    $assigneeId: ID
    $dueDate: Date
    $priority: String
    $linkEntities: Boolean
    $markDone: Boolean
  ) {
    mailCreateTask(
      threadId: $threadId
      title: $title
      description: $description
      assigneeId: $assigneeId
      dueDate: $dueDate
      priority: $priority
      linkEntities: $linkEntities
      markDone: $markDone
    ) {
      ok
      error
      errorCode
      taskId
    }
  }
`;

export const GrantMailboxAccessMutation = gql`
  mutation GrantMailboxAccess(
    $mailboxId: ID!
    $userId: ID!
    $enabled: Boolean!
    $canSend: Boolean
  ) {
    setMailboxAccess(
      mailboxId: $mailboxId
      userId: $userId
      enabled: $enabled
      canSend: $canSend
    ) {
      ok
      error
      errorCode
      count
    }
  }
`;

/** Подписанная ссылка на вложение письма почты (живёт 300 с). */
export const OpenMailAttachmentMutation = gql`
  mutation OpenMailAttachment($messageId: ID!, $index: Int!) {
    mailAttachmentLink(messageId: $messageId, index: $index) {
      ok
      error
      errorCode
      url
    }
  }
`;
